import { useState, useMemo, useCallback, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { BACKEND_URL } from '../config';
import { mediaUrl } from '../utils';

interface MediaCarouselProps {
  images: string[];
  videos: string[];
  strid: string;
}

export default function MediaCarousel({ images = [], videos = [], strid }: MediaCarouselProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const combinedMedia = useMemo(
    () => Array.from(new Set([...(videos ?? []), ...(images ?? [])])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [videos?.join('|'), images?.join('|')]
  );

  const generateVideoThumbnail = useCallback((videoSrc: string) => {
    return new Promise<string>((resolve, reject) => {
      const video = document.createElement('video');
      video.src = `${BACKEND_URL}/projects/${strid}/${videoSrc}`;
      video.crossOrigin = 'anonymous';
      video.addEventListener('loadeddata', () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')?.drawImage(video, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      });
      video.addEventListener('error', reject);
      video.load();
    });
  }, [strid]);

  useEffect(() => {
    const generate = async () => {
      const newThumbs: Record<string, string> = {};
      for (const media of combinedMedia) {
        if (media.includes('.mp4')) {
          try { newThumbs[media] = await generateVideoThumbnail(media); } catch {}
        }
      }
      setThumbnails(newThumbs);
    };
    generate();
  }, [combinedMedia, generateVideoThumbnail]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      if (e.key === 'ArrowLeft') setLightboxIdx(i => i !== null && i > 0 ? i - 1 : i);
      if (e.key === 'ArrowRight') setLightboxIdx(i => i !== null && i < images.length - 1 ? i + 1 : i);
    };
    if (lightboxIdx !== null) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIdx, images.length]);

  if (combinedMedia.length === 0) return null;

  const selected = combinedMedia[selectedIndex];
  const isVideo = selected?.includes('.mp4') || selected?.includes('youtube.com') || selected?.includes('youtu.be');

  const getThumbnail = (media: string) => {
    if (media.includes('.mp4')) return thumbnails[media] || '';
    if (media.includes('youtube.com') || media.includes('youtu.be')) {
      const videoId = media.split('youtube.com/watch?v=')[1] || media.split('youtu.be/')[1];
      const cleanId = videoId?.split('&')[0];
      return `https://img.youtube.com/vi/${cleanId}/hqdefault.jpg`;
    }
    return mediaUrl(media, `${BACKEND_URL}/projects/${strid}`) || '';
  };

  return (
    <div className="mt-8">
      <div className="relative bg-zinc-900 rounded-xl overflow-hidden border border-zinc-700">
        <div className="aspect-video flex items-center justify-center">
          {isVideo ? (
            <ReactPlayer url={selected.includes('http') ? selected : `${BACKEND_URL}/projects/${strid}/${selected}`} width="100%" height="100%" controls />
          ) : (
            <img
              src={mediaUrl(selected, `${BACKEND_URL}/projects/${strid}`) || ''}
              alt=""
              className="max-h-full max-w-full object-contain cursor-zoom-in"
              onClick={() => { const idx = images.indexOf(selected); if (idx !== -1) setLightboxIdx(idx); }}
            />
          )}
        </div>
        {combinedMedia.length > 1 && (
          <>
            <button
              onClick={() => setSelectedIndex((i) => (i > 0 ? i - 1 : combinedMedia.length - 1))}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-violet-600/60 text-white p-2 rounded-full transition-colors backdrop-blur-sm"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => setSelectedIndex((i) => (i < combinedMedia.length - 1 ? i + 1 : 0))}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-violet-600/60 text-white p-2 rounded-full transition-colors backdrop-blur-sm"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>

      {combinedMedia.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
          {combinedMedia.map((media, index) => (
            <button
              key={index}
              onClick={() => setSelectedIndex(index)}
              className={`relative flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                index === selectedIndex ? 'border-violet-500 shadow-md shadow-violet-500/20' : 'border-transparent opacity-50 hover:opacity-80'
              }`}
            >
              <img src={getThumbnail(media)} alt="" className="w-full h-full object-cover" />
              {(media.includes('.mp4') || media.includes('youtube.com') || media.includes('youtu.be')) && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <Play size={16} className="text-ink" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      {/* Lightbox backdrop */}
      {lightboxIdx !== null && (
        <div
          className="fixed top-16 left-0 right-0 bottom-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
          onClick={() => setLightboxIdx(null)}
        >
          {/* Modal box */}
          <div
            className="relative bg-zinc-900 rounded-2xl overflow-hidden shadow-2xl w-full max-w-[96vw] sm:max-w-[90vw] flex flex-col h-full"
            onClick={e => e.stopPropagation()}
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-ink/[0.06]">
              {images.length > 1 && (
                <span className="text-xs text-zinc-500">{lightboxIdx + 1} / {images.length}</span>
              )}
              <button
                className="ml-auto text-zinc-400 hover:text-ink p-1.5 rounded-lg hover:bg-ink/10 transition-colors"
                onClick={() => setLightboxIdx(null)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Image + nav */}
            <div className="relative flex items-center justify-center bg-zinc-950 flex-1 overflow-hidden min-h-0">
              <img
                src={mediaUrl(images[lightboxIdx], `${BACKEND_URL}/projects/${strid}`) || ''}
                alt=""
                className="max-h-full max-w-full object-contain p-4"
              />
              {lightboxIdx > 0 && (
                <button
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-300 hover:text-white p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                  onClick={e => { e.stopPropagation(); setLightboxIdx(i => i! - 1); }}
                  aria-label="Previous"
                >
                  <ChevronLeft size={22} />
                </button>
              )}
              {lightboxIdx < images.length - 1 && (
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-300 hover:text-white p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                  onClick={e => { e.stopPropagation(); setLightboxIdx(i => i! + 1); }}
                  aria-label="Next"
                >
                  <ChevronRight size={22} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
